import { Layer, Marker, Source } from 'react-map-gl/maplibre';
import type { GhostFeature } from './assistantTypes';
import { describeMetrics } from './applyAction';

interface AssistantGhostProps {
  features: GhostFeature[];
}

/** Above this many previews, per-feature labels stop being readable and become a grey smear —
 *  the chat bubble carries the names in a list anyway. */
const MAX_LABELS = 10;

/**
 * Dashed preview of proposed features. Rendered as a child of <Map> — Source/Layer only work
 * inside the map subtree — and kept in its own component so App.tsx gains one line rather than
 * ninety. Styling mirrors the manual drawing preview (`temp-polygon-line`) so a proposal reads as
 * "not saved yet" at a glance.
 *
 * Takes a LIST because a research answer proposes many features at once. Unticked ones are drawn
 * dimmed rather than removed: seeing where the three you rejected would have gone is most of the
 * value of reviewing a batch on the map instead of in a list.
 *
 * All polygons share one Source and all lines another, rather than a Source per feature. Fifty
 * sources means fifty style-layer round trips on every tick of a checkbox; one FeatureCollection
 * with a data-driven paint expression re-renders as a single diff.
 *
 * Read-only in v1: Confirm and Discard live in the chat bubble, next to the sentence that explains
 * what is being proposed.
 */
export default function AssistantGhost({ features }: AssistantGhostProps) {
  const drawn = (features || []).filter((f) => f.coordinates?.length);
  if (!drawn.length) return null;

  const showLabels = drawn.length <= MAX_LABELS;
  const points = drawn.filter((f) => f.kind === 'point');
  const polygons = drawn.filter((f) => f.kind === 'polygon');
  const lines = drawn.filter((f) => f.kind === 'line');

  const closed = (coords: number[][]) => {
    const first = coords[0];
    const last = coords[coords.length - 1];
    return first[0] === last[0] && first[1] === last[1] ? coords : [...coords, first];
  };

  const properties = (feature: GhostFeature) => ({
    color: feature.color,
    // MapLibre expressions cannot read a boolean reliably across style versions; 1/0 always works.
    on: feature.selected === false ? 0 : 1,
  });

  const polygonData = {
    type: 'FeatureCollection' as const,
    features: polygons.map((f) => ({
      type: 'Feature' as const,
      properties: properties(f),
      geometry: { type: 'Polygon' as const, coordinates: [closed(f.coordinates)] },
    })),
  };

  const lineData = {
    type: 'FeatureCollection' as const,
    features: [
      ...polygons.map((f) => ({
        type: 'Feature' as const,
        properties: properties(f),
        geometry: { type: 'LineString' as const, coordinates: closed(f.coordinates) },
      })),
      ...lines.map((f) => ({
        type: 'Feature' as const,
        properties: properties(f),
        geometry: { type: 'LineString' as const, coordinates: f.coordinates },
      })),
    ],
  };

  const dimmed = (on: number, off: number) => ['case', ['==', ['get', 'on'], 1], on, off];

  return (
    <>
      {polygons.length > 0 && (
        <Source id="assistant-ghost-fill-src" type="geojson" data={polygonData}>
          <Layer
            id="assistant-ghost-fill"
            type="fill"
            paint={{
              'fill-color': ['get', 'color'],
              'fill-opacity': dimmed(0.15, 0.03) as never,
            }}
          />
        </Source>
      )}

      {lineData.features.length > 0 && (
        <Source id="assistant-ghost-line-src" type="geojson" data={lineData}>
          <Layer
            id="assistant-ghost-line"
            type="line"
            paint={{
              'line-color': ['get', 'color'],
              'line-width': dimmed(2.5, 1.5) as never,
              'line-opacity': dimmed(1, 0.35) as never,
              'line-dasharray': [2, 1],
            }}
          />
        </Source>
      )}

      {points.map((feature) => {
        const [lng, lat] = feature.coordinates[0];
        const off = feature.selected === false;
        return (
          <Marker
            key={feature.featureId || feature.proposalId}
            longitude={lng}
            latitude={lat}
            anchor="bottom"
          >
            <div
              className={`assistant-ghost-pin${off ? ' assistant-ghost-off' : ''}`}
              style={{ borderColor: feature.color }}
              title={feature.name}
            >
              <span className="assistant-ghost-pin-dot" style={{ background: feature.color }} />
              {showLabels && <span className="assistant-ghost-label">{feature.name}</span>}
            </div>
          </Marker>
        );
      })}

      {showLabels && [...polygons, ...lines].map((feature) => {
        const centroid = feature.metrics?.centroid;
        if (!centroid) return null;
        const size = describeMetrics(feature.kind, feature.metrics);
        return (
          <Marker
            key={`lbl-${feature.featureId || feature.proposalId}`}
            longitude={centroid.lng}
            latitude={centroid.lat}
            anchor="center"
          >
            <div
              className={`assistant-ghost-label${feature.selected === false ? ' assistant-ghost-off' : ''}`}
              style={{ borderColor: feature.color }}
            >
              {size ? `${feature.name} · ${size}` : feature.name}
            </div>
          </Marker>
        );
      })}
    </>
  );
}
